import { CheckCircle, Clock, AlertTriangle, XCircle, AlertOctagon, ShieldAlert } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// T186: Updated to include 'conflict' status for double-booking detection
type BookingPushStatus = 'pending' | 'pushing' | 'success' | 'failed' | 'escalated' | 'conflict';

interface BookingPushStatusBadgeProps {
  status: BookingPushStatus;
  className?: string;
}

export function BookingPushStatusBadge({ status, className }: BookingPushStatusBadgeProps): JSX.Element {
  switch (status) {
    case 'pending':
      return (
        <Badge variant="outline" className={className} data-testid="booking-push-status-pending">
          <Clock className="mr-1 h-3 w-3" />
          Pending
        </Badge>
      );
    case 'pushing':
      return (
        <Badge variant="secondary" className={className} data-testid="booking-push-status-pushing">
          <Clock className="mr-1 h-3 w-3 animate-spin" />
          Pushing
        </Badge>
      );
    case 'success':
      return (
        <Badge variant="success" className={className} data-testid="booking-push-status-success">
          <CheckCircle className="mr-1 h-3 w-3" />
          Success
        </Badge>
      );
    case 'failed':
      return (
        <Badge variant="destructive" className={className} data-testid="booking-push-status-failed">
          <XCircle className="mr-1 h-3 w-3" />
          Failed
        </Badge>
      );
    case 'escalated':
      return (
        <Badge variant="destructive" className={className} data-testid="booking-push-status-escalated">
          <AlertOctagon className="mr-1 h-3 w-3" />
          Escalated
        </Badge>
      );
    // T186: Conflict status for double-booking detection (T176)
    case 'conflict':
      return (
        <Badge
          variant="destructive"
          className={cn('animate-pulse', className)}
          data-testid="booking-push-status-conflict"
        >
          <ShieldAlert className="mr-1 h-3 w-3" />
          Conflict
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className={className}>
          <AlertTriangle className="mr-1 h-3 w-3" />
          Unknown
        </Badge>
      );
  }
}

/**
 * T186: Badge component for booking modification status.
 * Used to show if a booking modification was successfully pushed to partner.
 */
type ModificationStatus = 'pending' | 'success' | 'failed';

interface BookingModificationStatusBadgeProps {
  status: ModificationStatus;
  className?: string;
}

export function BookingModificationStatusBadge({
  status,
  className,
}: BookingModificationStatusBadgeProps): JSX.Element {
  switch (status) {
    case 'pending':
      return (
        <Badge variant="secondary" className={className} data-testid="booking-modification-status-pending">
          <Clock className="mr-1 h-3 w-3" />
          Modification Pending
        </Badge>
      );
    case 'success':
      return (
        <Badge variant="success" className={className} data-testid="booking-modification-status-success">
          <CheckCircle className="mr-1 h-3 w-3" />
          Modified
        </Badge>
      );
    case 'failed':
      return (
        <Badge variant="destructive" className={className} data-testid="booking-modification-status-failed">
          <XCircle className="mr-1 h-3 w-3" />
          Modification Failed
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className={className}>
          <AlertTriangle className="mr-1 h-3 w-3" />
          Unknown
        </Badge>
      );
  }
}
